import CryptoJS from 'crypto-js';

// Generate a unique encryption key per channel (shared by all participants)
const generateEncryptionKey = (channelId: string): string => {
  // In production, this should be derived from a secure key exchange
  // For now, we use a combination of channelId and a secret
  // This ensures all participants in a channel use the same key
  const SECRET_KEY = import.meta.env.VITE_ENCRYPTION_SECRET || 'deployment-system-secret-key-2025';
  return CryptoJS.SHA256(channelId + SECRET_KEY).toString();
};

// Store encryption keys in session storage (more secure than localStorage)
const getStoredKey = (channelId: string): string => {
  const key = sessionStorage.getItem(`chat_key_${channelId}`);
  if (!key) {
    const newKey = generateEncryptionKey(channelId);
    sessionStorage.setItem(`chat_key_${channelId}`, newKey);
    return newKey;
  }
  return key;
};

/**
 * Encrypt a message using AES-256 encryption
 * @param message - Plain text message to encrypt
 * @param channelId - Channel ID for key generation (shared by all participants)
 * @returns Encrypted message string
 */
export const encryptMessage = (message: string, channelId: string): string => {
  try {
    const key = getStoredKey(channelId);
    const encrypted = CryptoJS.AES.encrypt(message, key).toString();
    return encrypted;
  } catch (error) {
    console.error('Encryption error:', error);
    return message; // Fallback to plain text if encryption fails
  }
};

/**
 * Decrypt a message using AES-256 decryption
 * @param encryptedMessage - Encrypted message string
 * @param channelId - Channel ID for key retrieval (shared by all participants)
 * @returns Decrypted plain text message
 */
export const decryptMessage = (encryptedMessage: string, channelId: string): string => {
  // If message is empty or undefined, return as is
  if (!encryptedMessage) return encryptedMessage;
  
  // Check if message looks like it's encrypted (contains base64-like characters)
  // AES encrypted messages typically contain +, /, = characters
  const looksEncrypted = /[+/=]/.test(encryptedMessage) || encryptedMessage.includes('U2FsdGVk');
  
  // If it doesn't look encrypted, return as is (probably plain text)
  if (!looksEncrypted) {
    return encryptedMessage;
  }
  
  try {
    const key = getStoredKey(channelId);
    const decrypted = CryptoJS.AES.decrypt(encryptedMessage, key);
    const plainText = decrypted.toString(CryptoJS.enc.Utf8);
    
    // If decryption fails or returns empty, return original
    if (!plainText || plainText.trim() === '') {
      console.warn('Decryption returned empty string, returning original');
      return encryptedMessage;
    }
    
    return plainText;
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedMessage; // Return original if decryption fails
  }
};

/**
 * Encrypt file metadata
 * @param fileName - Original file name
 * @param channelId - Channel ID for key generation
 * @returns Encrypted file name
 */
export const encryptFileName = (fileName: string, channelId: string): string => {
  try {
    const key = getStoredKey(channelId);
    const encrypted = CryptoJS.AES.encrypt(fileName, key).toString();
    // Replace special characters that might cause issues in URLs
    return encrypted.replace(/\//g, '_').replace(/\+/g, '-');
  } catch (error) {
    console.error('File name encryption error:', error);
    return fileName;
  }
};

/**
 * Decrypt file metadata
 * @param encryptedFileName - Encrypted file name
 * @param channelId - Channel ID for key retrieval
 * @returns Decrypted file name
 */
export const decryptFileName = (encryptedFileName: string, channelId: string): string => {
  try {
    const key = getStoredKey(channelId);
    // Reverse URL-safe replacements
    const normalized = encryptedFileName.replace(/_/g, '/').replace(/-/g, '+');
    const decrypted = CryptoJS.AES.decrypt(normalized, key);
    const plainText = decrypted.toString(CryptoJS.enc.Utf8);
    return plainText || encryptedFileName;
  } catch (error) {
    console.error('File name decryption error:', error);
    return encryptedFileName;
  }
};

/**
 * Clear encryption keys (use on logout)
 */
export const clearEncryptionKeys = (channelId: string): void => {
  sessionStorage.removeItem(`chat_key_${channelId}`);
};

/**
 * Clear all chat encryption keys (use on logout)
 */
export const clearAllChatKeys = (): void => {
  const keys = Object.keys(sessionStorage);
  keys.forEach(key => {
    if (key.startsWith('chat_key_')) {
      sessionStorage.removeItem(key);
    }
  });
};

/**
 * Generate a secure random key for key exchange
 */
export const generateSecureKey = (): string => {
  return CryptoJS.lib.WordArray.random(256/8).toString();
};
